import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    CleaningServices,
    CalendarToday,
    ShoppingCart,
    Shield,
    ReportProblem,
    DoneAll,
    Group,
    CheckCircleOutline,
    FormatListNumbered,
    Close,
    Replay,
    HelpOutline
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェア掃除問題のリアル
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('frequency')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    掃除頻度の目安とコツ
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('toban')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    掃除当番制の作り方＆運用方法
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('goods')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    共同購入すべき掃除グッズリスト
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('prevention')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    汚れ予防のテクニック
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                    6.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('trouble')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    掃除トラブルの解決法
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア掃除問題のリアル
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「誰が掃除するの？」「なんで私ばかり…」――。ルームシェアの最大のストレス要因の一つが、共有スペースの掃除です。リビングやキッチン、バス・トイレなど、みんなで使う空間をいかにキレイに保つかは、快適な共同生活の大前提。ですが、掃除の頻度や分担、やり方の違いで思わぬトラブルが発生しがち。「掃除問題」をうまく乗り越えれば、ルームシェアの楽しさや居心地の良さが格段にUPします。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            本記事では、共有スペースを清潔に保つための掃除頻度・当番制の作り方・おすすめグッズ・予防策・トラブル解決法まで、実践的なノウハウを徹底解説！最新の傾向やリアルな困りごとにも触れつつ、「ラクしてキレイ」を叶えるコツをお届けします。ルームシェア経験者もこれから始める方も、ぜひ参考にしてください。
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Chip icon={<CleaningServices />} label="掃除頻度" color="primary" variant="outlined" />
                            <Chip icon={<CalendarToday />} label="当番制" color="primary" variant="outlined" />
                            <Chip icon={<Shield />} label="トラブル回避" color="primary" variant="outlined" />
                            <Chip icon={<ShoppingCart />} label="おすすめグッズ" color="primary" variant="outlined" />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                            <img
                                src="/images/blog/modern-share-living_roomshare-cleaning-tips.jpg"
                                alt="明るい共有リビングで複数のルームメイトが談笑しながら掃除を協力して行い、全員がリラックスした雰囲気で過ごしている様子。ナチュラルウッドの家具と観葉植物がアクセントになっている"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 掃除頻度 */}
            <Box id="frequency" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    掃除頻度の目安とコツ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共有部の清潔感は、頻度と“ちょっとした気配り”がカギ。どのくらいのペースが理想？という疑問に、実際のシェアハウス運営データや住人の声をもとに「無理なく続く頻度の目安」と効率的な掃除のポイントをまとめました。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <CalendarToday sx={{ mr: 1 }} />
                        主要スペース別・掃除頻度の目安
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <DoneAll color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="リビング・ダイニング：週1回"
                                secondary="集まる頻度が高い場合は週2回が理想"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <DoneAll color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="キッチン：週2回＋汚したらすぐ"
                                secondary="油汚れ・水回りは溜めずにサッと掃除を"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <DoneAll color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="バス・トイレ：週1〜2回"
                                secondary="髪の毛や水垢はこまめにチェック"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <DoneAll color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="玄関・廊下：2週間に1回"
                                secondary="土埃や靴跡が目立つ場合は都度サッと"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>Point:</b> 「汚れをためない習慣」が最大のコツ！目についたら“ついで掃除”を意識すると、負担も最小限になります。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/share-kitchen-cleaning_roomshare-cleaning-tips.jpg"
                        alt="住人がキッチンを協力して掃除し、シンクや調理台を丁寧に拭きあげている様子。明るい照明と清潔感のある白いタイルが印象的"
                        style={{ width: '100%', borderRadius: 8, boxShadow: 1 }}
                    />
                </Box>
            </Box>

            {/* 当番制 */}
            <Box id="toban" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    掃除当番制の作り方＆運用方法
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「誰がいつどこを掃除するか」を明確に決めるだけで、掃除のストレスは大幅減！ルームシェアの定番・掃除当番制の作り方と、実際によく使われている運用方法を紹介します。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormatListNumbered sx={{ mr: 1 }} />
                        当番制導入の5ステップ
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <Group color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="1. 担当エリアを明確に"
                                secondary="リビング、キッチン、バストイレなど、掃除箇所を具体的に分ける"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CalendarToday color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="2. 順番を決めて表にする"
                                secondary="カレンダーやホワイトボードで見える化。アプリ活用も◎"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Replay color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="3. ローテーション方式を採用"
                                secondary="1週間ごと・1ヶ月ごとなど、負担を平等に回す"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="4. 終わったらチェック"
                                secondary="簡単な記録やスタンプで達成感UP＆やり忘れ防止"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <HelpOutline color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="5. ルールは定期的に見直す"
                                secondary="人数や生活リズムの変化に合わせて柔軟に調整"
                            />
                        </ListItem>
                    </List>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2">
                        <b>おすすめアプリ：</b>「LINE」「Googleカレンダー」「家事分担アプリ」など、全員が見やすいツールを活用しましょう。
                    </Typography>
                </Paper>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/share-cleaning-schedule_roomshare-cleaning-tips.jpg"
                        alt="カラフルなホワイトボードに掃除当番のスケジュールが一覧表で記入され、ルームメイトの名前と担当場所が見やすく並んでいる。明るい朝の光が差し込むダイニングに設置"
                        style={{ width: '100%', borderRadius: 8, boxShadow: 1 }}
                    />
                </Box>
                <Typography variant="body2">
                    <b>トラブル予防TIP：</b>最初に「できない週は交代OK」「どうしても苦手な場所は相談」など、柔軟なルールを合意しておくと揉めにくいです。
                </Typography>
            </Box>

            {/* グッズリスト */}
            <Box id="goods" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    共同購入すべき掃除グッズリスト
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    掃除グッズは「みんなで管理」「補充しやすい」がポイント。ムダ買いを防ぎつつ、清潔感UPに効果的なアイテムを厳選しました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <ShoppingCart sx={{ mr: 1 }} />
                                必須アイテム
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="使い捨て掃除シート／ウェットティッシュ"
                                        secondary="床・テーブル・水回りなど万能"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="トイレクリーナー・お風呂用洗剤"
                                        secondary="衛生管理に必須"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="スポンジ・ブラシ各種"
                                        secondary="キッチン、バス、シンク用に分けて"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="ゴミ袋（大・小）"
                                        secondary="交換・補充を忘れずに"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="コロコロクリーナー"
                                        secondary="リビングや玄関マットに便利"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                <Shield sx={{ mr: 1 }} />
                                あると便利なアイテム
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="除菌スプレー"
                                        secondary="ドアノブやテーブル、ウイルス対策にも"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="使い捨て手袋"
                                        secondary="トイレ・ゴミ出し時に"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="掃除機（コードレス推奨）"
                                        secondary="週1回の本格掃除や急な来客時に"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CleaningServices color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="芳香剤・消臭剤"
                                        secondary="玄関、トイレ、リビングに"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2">
                    <b>補充ルール例：</b>「最後に使った人がメモ」「月末にまとめ買い」など管理ルールも決めておくと安心です。
                </Typography>
            </Box>

            {/* 汚れ予防 */}
            <Box id="prevention" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    汚れ予防のテクニック
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    大掃除より「汚れをためない仕組み」がラク＆清潔感UPの近道！毎日できる予防テクを紹介します。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <Shield color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="玄関マットで砂埃をカット"
                                secondary="靴の汚れを部屋に持ち込まない"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Shield color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="キッチンにアルコールスプレー常備"
                                secondary="調理後すぐ拭き掃除でベタつき防止"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Shield color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="バス・トイレの水滴をサッと拭き取る"
                                secondary="カビ・水垢を根本予防"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Shield color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="“自分のゴミは自分で捨てる”を徹底"
                                secondary="共用ゴミ箱の山積みを防止"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>豆知識：</b>「掃除しやすい収納」や「物を増やさない工夫」もルームシェアでは効果大。みんながサッと片付けやすい仕組みを作りましょう。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/share-cleaning-prevention_roomshare-cleaning-tips.jpg"
                        alt="カラフルな玄関マットと並ぶシンプルな靴棚、アルコールスプレーが置かれたキッチンカウンターなど、汚れを予防する工夫が随所に見られる明るい共有空間"
                        style={{ width: '100%', borderRadius: 8, boxShadow: 1 }}
                    />
                </Box>
            </Box>

            {/* トラブル解決 */}
            <Box id="trouble" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    掃除トラブルの解決法
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「ちゃんと掃除してくれない」「ルールを守らない」など、掃除にまつわるトラブルは意外と多いもの。よくある悩みと、実際に効果があった解決法をまとめました。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#ffe0b2' }}>
                    <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <ReportProblem sx={{ mr: 1, color: '#fb8c00' }} />
                        よくあるトラブル
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <ReportProblem color="warning" />
                            </ListItemIcon>
                            <ListItemText
                                primary="掃除のやり方・基準が人によって違う"
                                secondary="“自分ではキレイ”と思っていても、他の人には不十分な場合も"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ReportProblem color="warning" />
                            </ListItemIcon>
                            <ListItemText
                                primary="掃除をサボる人がいて不公平感"
                                secondary="当番を守らない、気付いてもやらない…"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ReportProblem color="warning" />
                            </ListItemIcon>
                            <ListItemText
                                primary="“やった・やってない”の水掛け論"
                                secondary="記録がないと、曖昧な責任問題に発展"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="h6" sx={{ my: 2, display: 'flex', alignItems: 'center' }}>
                    <DoneAll color="success" sx={{ mr: 1 }} />
                    解決のためのポイント
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="掃除ルールを“見える化”"
                            secondary="表やアプリで「いつ・誰が・どこを掃除」を共有"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="“ありがとう”の声かけを習慣に"
                            secondary="小さな感謝がモチベーション維持に"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="掃除後の“チェックリスト”を活用"
                            secondary="やり忘れ・やったつもりを防止"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="話し合いの場を定期的に持つ"
                            secondary="不満や意見をためずに伝える機会を作る"
                        />
                    </ListItem>
                </List>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    <b>まとめ：</b>掃除トラブルは「仕組み」と「コミュニケーション」でほぼ防げます。トラブルが起きても、感情的にならず“仕組みの見直し”を最優先にしましょう。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/share-trouble-meeting_roomshare-cleaning-tips.jpg"
                        alt="ダイニングテーブルを囲んで、複数のルームメイトが和やかに掃除について話し合っている様子。全員がメモやスマホを手に持ち、前向きな雰囲気でコミュニケーションを取っている"
                        style={{ width: '100%', borderRadius: 8, boxShadow: 1 }}
                    />
                </Box>
            </Box>

            {/* まとめ */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：掃除問題をクリアして最高のルームシェアを
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                    ルームシェアは、掃除の悩みを「みんなで解決」できるチャンスでもあります。日々のちょっとした工夫と、仕組み化・コミュニケーションを大切にすることで、快適でハッピーな共同生活が実現します。ぜひ本記事のノウハウを取り入れて、“気持ちよく暮らせるシェア生活”をスタートさせてください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-cleaning-tips',
    slug: 'roomshare-cleaning-tips',
    title: 'ルームシェアの掃除問題を解決！共有スペースをキレイに保つコツと当番制の作り方',
    excerpt: '掃除頻度の目安\n当番制の作り方\n共同購入グッズリスト\n汚れ予防策\n掃除トラブル解決法',
    coverImage: '/images/blog/cover-modern-share-living_roomshare-cleaning-tips.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ナチュラルウッドの家具や観葉植物があり、温かく快適な空間が広がっている',
    publishedAt: '2024-06-13T12:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '掃除・家事', 'ライフスタイル'],
    tags: ['ルームシェア', '掃除', '当番制', '共有スペース', 'トラブル対策'],
    author: {
        name: '高橋 祐介',
        avatar: '/images/avatars/yusuke-takahashi.jpg',
        bio: 'ルームシェア歴10年以上。これまで10軒以上のシェアハウスで生活し、快適な共同生活とトラブル回避ノウハウを多数発信中。家事分担やコミュニケーションの専門ブログ運営。',
        profilePicture: '/images/avatars/yusuke-takahashi.jpg'
    },
    viewCount: 1875,
    contentComponent: PostContent
};

export default post;